import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useListDestinationOptions } from '@/features/pipelines/destinations/hooks';
import { useListSourceOptions } from '@/features/pipelines/sources/hooks';
import { getHydraUIAPIQueryOptions, hydraUiApi } from '@/services/api';
import type { DestinationResult, SourceResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, COMMAND_DIALOG_QUERY_DEFAULTS } from './constants';
import { buildIntegrationCatalog, type IntegrationCatalog } from './utils/integration-catalog';
import { filterAndCapIntegrations, mapPipelinesConnectionToSearchResult } from './utils/pipelines-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseIntegrationsSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type IntegrationsSource = SearchSource<SourceResult | DestinationResult> & {
  catalog: IntegrationCatalog;
};

export function useIntegrationsSource({
  workspaceId,
  enabled,
  query,
}: UseIntegrationsSourceOptions): IntegrationsSource {
  const catalogWorkspaceId = workspaceId > 0 ? workspaceId : null;
  const sourceOptionsQuery = useListSourceOptions(catalogWorkspaceId);
  const destinationOptionsQuery = useListDestinationOptions(catalogWorkspaceId);

  // Each query resolves independently; the map is partial until both settle.
  // During that window, types from the slower catalog show fallback icons/names briefly.
  const catalog = useMemo<IntegrationCatalog>(
    () => buildIntegrationCatalog(sourceOptionsQuery.data ?? [], destinationOptionsQuery.data ?? []),
    [sourceOptionsQuery.data, destinationOptionsQuery.data],
  );

  const integrationsQuery = useQuery(
    getHydraUIAPIQueryOptions({
      queryKey: ['listIntegrationsPipelines', { params: { environment_id: workspaceId } }],
      queryFn: ({ signal }) =>
        hydraUiApi.integrations.getPipelines({
          params: { path: { environment_id: String(workspaceId) } },
          signal,
        }),
      queryConfig: {
        enabled,
        ...COMMAND_DIALOG_QUERY_DEFAULTS,
        // The endpoint returns all connections with no server-side filter or pagination.
        // Mapping is cheap; the render-side cap is applied after fuzzy-filtering so
        // destinations aren't dropped on workspaces with many sources.
        select: (data) => data.connections.map(mapPipelinesConnectionToSearchResult),
      },
    }),
  );

  const results = useMemo(
    () => filterAndCapIntegrations(integrationsQuery.data ?? [], query, catalog, CLIENT_SIDE_RESULT_CAP),
    [integrationsQuery.data, query, catalog],
  );

  return { ...integrationsQuery, results, catalog };
}
